import type { SubscriptionTier } from "@/lib/api";
import type { PlanCardCta } from "@/components/billing/PlanCard";
import { type PlanCardId, type SubscribablePlan } from "./planConfig";

const PLAN_TIER_RANK: Record<SubscriptionTier, number> = {
  trial: 0,
  partner_trial: 0,
  vip_trial: 0,
  pro_trial: 0,
  locked_free: 0,
  starter: 1,
  pro: 2,
  enterprise: 3,
  fairgen_internal: 4,
};

export interface UsePlanActionsArgs {
  currentTier: SubscriptionTier;
}

export interface UsePlanActions {
  ctaForPlan: (plan: SubscribablePlan) => PlanCardCta;
  downgradeHint: (plan: SubscribablePlan) => string | null;
  handleCta: (cardId: PlanCardId, action: PlanCardCta["kind"]) => void;
}

/**
 * Shared CTA logic for the Plan & Usage card row. Self-serve checkout is
 * retired for the duration of the payments-provider migration: every kind
 * other than "contact-sales" sits behind a disabled control (see
 * `BillingLockedTooltip`) and is handled here as a safe no-op.
 */
export function usePlanActions(args: UsePlanActionsArgs): UsePlanActions {
  const { currentTier } = args;

  function ctaForPlan(plan: SubscribablePlan): PlanCardCta {
    if (plan === currentTier) return { kind: "active" };
    const currentRank = PLAN_TIER_RANK[currentTier] ?? 0;
    const targetRank = PLAN_TIER_RANK[plan] ?? 0;
    // Users on trial / locked_free have no paid tier yet, so show "Select"
    // rather than upgrade/downgrade since there's nothing to compare against.
    if (currentRank === 0) return { kind: "start" };
    return targetRank > currentRank
      ? { kind: "upgrade" }
      : { kind: "downgrade" };
  }

  function downgradeHint(plan: SubscribablePlan): string | null {
    // Downgrading from Enterprise is not self-serve (LS billing model
    // mismatch). Direct users to support instead of a portal-based downgrade.
    if (currentTier === "enterprise" && plan !== "enterprise") {
      return "Contact support to downgrade.";
    }
    return null;
  }

  function handleCta(_cardId: PlanCardId, _action: PlanCardCta["kind"]): void {
    // Deliberate no-op kept only to satisfy the PlanCard contract: PlanCard
    // owns contact-sales navigation itself, and every other kind sits behind
    // a disabled control while self-serve checkout is locked.
  }

  return {
    ctaForPlan,
    downgradeHint,
    handleCta,
  };
}
